import type { FloraEditor } from '@/editor/FloraEditor';
import type { ContourLabelMode } from '@/editor/ContourOverlay';
import type { TerrainEditor, TerrainToolKind } from '@/editor/TerrainEditor';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { seasonBlend } from '@/environment/SeasonModel';
import type { SurfaceCoverage, SurfaceLayer } from '@/terrain/TerrainSurface';
import type { NavAction } from '@/rendering/CameraController';
import { el } from './dom';
import { Toolbar, type ActiveToolName, type ToolbarAction } from './Toolbar';
import { TerrainPanel } from './TerrainPanel';
import { FloraBrowser } from './FloraBrowser';
import { FaunaBrowser } from './FaunaBrowser';
import type { FaunaCatalog } from '@/fauna/FaunaCatalog';
import type { PropCatalog } from '@/site/PropCatalog';
import { ObjectBrowser } from './ObjectBrowser';
import { FloraDatabasePanel } from './FloraDatabasePanel';
import { unlocalizedResolver } from '@/localization/FloraResolver';
import { zoneLabel } from '@/localization';
import { HelpMenu } from './HelpMenu';
import type { AboutFacts } from './About';
import type { FeedbackChannel } from './Feedback';
import { InspectorPanel, type SiteSummary } from './InspectorPanel';
import { EnvironmentPanel } from './EnvironmentPanel';
import type { LocalizeSectionDeps } from './LocalizeSection';
import { SitePanel } from './SitePanel';
import { LicensePanel, type LicensePanelDeps } from './LicensePanel';
import type { FloraResolver } from '@/localization/FloraResolver';
import { ChronoBar } from './ChronoBar';
import { HistoryPanel } from './HistoryPanel';
import { StatusBar, type SaveIndicator } from './StatusBar';
import {
  ToolCursor,
  navCursorKind,
  plantCursorKind,
  sculptCursorKind,
  type CursorKind,
} from './ToolCursor';
import { PLANT_SHORTCUTS, SCULPT_SHORTCUTS, ToolHint, type ShortcutHint } from './ToolHint';
import { plantModeLabel } from '@/editor/PlantBrush';
import type { AutosaveStatus } from '@/project/AutosaveService';
import { UiPreferences } from './UiPreferences';
import type { SemanticProfile } from '@/semantics/SemanticProfile';

/**
 * The HUD shell (spec §19): top toolbar, left flora palette, right inspector + environment tray, and
 * a bottom status bar, composed over the 3D canvas.
 *
 * The HUD is a *view* over the editor and environment managers — it owns no domain state. Every
 * mutation it triggers goes through an editor command or an environment setter, so undo/redo and
 * persistence keep working whether a change came from the HUD or from a keyboard shortcut.
 *
 * Refreshes are pull-based and coalesced into an animation frame: the editor fires one change
 * notification, the HUD re-reads whatever it needs. That avoids a web of fine-grained bindings for a
 * panel set this small.
 */

export type CameraViewName = 'perspective' | 'top' | 'presentation';

/**
 * Toolbar actions that change what you can see rather than what holds the canvas.
 *
 * The history panel is one of them: stepping back three strokes with the brush still in hand is the
 * normal way to use it, so opening it must not put the brush down.
 */
const VIEW_AID_ACTIONS: readonly ToolbarAction[] = ['grid', 'topo', 'history'];

export interface HudDeps {
  container: HTMLElement;
  editor: FloraEditor;
  /**
   * The 3D canvas. Given one, the HUD draws the pointer itself — a glyph per tool, following the
   * mouse over the canvas. Optional so a headless/test HUD can be built without it.
   */
  canvas?: HTMLElement;
  /**
   * The fauna palette's wiring. Absent leaves the palette unbuilt — see the note at its construction.
   */
  fauna?: {
    catalog: FaunaCatalog;
    /** What the staffage tool currently has armed, so the palette shows it selected. */
    armed: () => { definitionId: string; poseId: string } | null;
    onPick: (definitionId: string, poseId: string) => void;
  };
  /** The object palette's wiring. Same contract. */
  objects?: {
    catalog: PropCatalog;
    armed: () => string | null;
    onPick: (propId: string) => void;
  };
  /** The ground tools. Optional so a headless/test HUD can be built without them. */
  terrainEditor?: TerrainEditor;
  /** Switch the ground tool on or off. `null` hands the canvas back to the flora editor. */
  onGroundTool?: (kind: TerrainToolKind | null) => void;
  /** Fraction of the site under each ground material, for the paint panel's coverage readout. */
  groundCoverage?: () => SurfaceCoverage | null;
  /**
   * Open the dialog that makes a new ground material. Omitted leaves the paint panel with the five
   * that ship in code and no dead button.
   */
  onNewMaterial?: () => void;
  /** Retire a material this site added, taking its paint back out of the ground. */
  onRetireMaterial?: (layer: SurfaceLayer) => void;
  environment: EnvironmentManager;
  onSetView: (view: CameraViewName) => void;
  onFocusSelection: () => void;
  /** Toggle the magic wand. Optional so a headless/test HUD can be built without it. */
  onWandTool?: (active: boolean) => void;
  /** Whether the wand currently holds the canvas, so the toolbar can show it pressed. */
  wandActive?: () => boolean;
  /** Toggle the semantic tag tool. Optional so a headless/test HUD can be built without it. */
  onTagTool?: (active: boolean) => void;
  /** Whether the tag tool holds the canvas, so the toolbar can show it pressed. */
  tagActive?: () => boolean;
  /** Everything an AI render will be told about one plant, for the inspector's meaning section. */
  semanticsFor?: (instanceId: string) => SemanticProfile | null;
  /** Whole-site semantics, for the inspector's no-selection view. */
  siteSemantics?: () => SemanticProfile | null;
  /** Show or hide the reference grid overlay. */
  onToggleGrid?: () => void;
  /** Whether the reference grid is currently drawn, so the toolbar can show it pressed. */
  gridVisible?: () => boolean;
  /** Show or hide the topographic contour overlay. */
  onToggleTopo?: () => void;
  /** Whether the contour map is currently drawn, so the toolbar can show it pressed. */
  topoVisible?: () => boolean;
  /** Step the contour elevation figures through auto → always → off. */
  onCycleTopoLabels?: () => void;
  /** How the contour figures are being shown, for the sculpt panel's control. */
  topoLabelMode?: () => ContourLabelMode;
  /** Vertical spacing between contours in metres, for the sculpt panel's readout. */
  topoIntervalMeters?: () => number;
  /** Spacing between the numbered contours, or zero when every line is numbered. */
  topoIndexIntervalMeters?: () => number;
  onNewSite?: () => void;
  onOpen?: () => void;
  onSave?: () => void;
  onSaveAs?: () => void;
  onRename?: () => void;
  onTogglePhotoMode?: () => void;
  /** Open (or close) the full-sheet shots browser over the designer. */
  onOpenShots?: () => void;
  /** Current site dimensions for the inspector's canvas summary. */
  site?: () => SiteSummary | null;
  /** Smoothed frames-per-second for the status bar. */
  fps?: () => number;
  /** Existing readout element to adopt into the status bar (the one given to the editor). */
  readout?: HTMLElement;
  /**
   * Where the site is, and how to say so — mounted as a bar directly above the flora palette, since
   * localizing changes what every tile in it says. Omitted leaves the palette with no badges and no
   * site bar at all.
   */
  localization?: LocalizeSectionDeps;
  /**
   * What the account is entitled to and whether there is an OpenAI key — mounted directly under the
   * site bar, because "can this app render" is the other question a user has before they start, and
   * it has the same answer-it-once-and-forget-it shape as localizing. Omitted leaves no panel at all.
   */
  license?: Omit<LicensePanelDeps, 'collapsed'>;
  /**
   * The site's flora resolver, so the palette can rate each plant against this climate. Given
   * together with {@link localization} or not at all.
   */
  floraResolver?: FloraResolver;
  /** Build string stamped onto a catalog suggestion raised from the database. */
  appVersion?: string;
  /**
   * Where reports and suggestions go — the flora database's and the toolbar's alike, so a
   * deployment points them somewhere real in one place. Defaults to the local queue.
   */
  feedback?: FeedbackChannel;
  /**
   * Where the open site is kept, in one phrase — "this browser", "your account". Shown in the info
   * sheet, because "where is my work" is the question a version number is usually asked alongside.
   */
  storageLabel?: () => string;
  /**
   * Reopen the guided tour, offered from the info sheet. Absent on a host that has none.
   */
  onTour?: () => void;
}

export class Hud {
  readonly root: HTMLElement;
  readonly preferences = new UiPreferences();

  private readonly toolbar: Toolbar;
  private readonly browser: FloraBrowser;
  /** The two palettes below the flora one — see `docs/PLACEABLE_CLASSES.md`. */
  private readonly faunaBrowser: FaunaBrowser | null;
  private readonly objectBrowser: ObjectBrowser | null;
  /**
   * The flora database. Built eagerly but mounted hidden: it costs one DOM subtree and no WebGL
   * context until it is opened, and the palette's header needs something to point at from the start.
   */
  private readonly database: FloraDatabasePanel;
  private readonly inspector: InspectorPanel;
  private readonly environmentPanel: EnvironmentPanel;
  /** Where the site is — the bar above the palette. Null when the host offers no localization. */
  private readonly sitePanel: SitePanel | null;
  /** Licence tier and OpenAI key, directly under the site bar. Null when the host offers neither. */
  private readonly licensePanel: LicensePanel | null;
  private readonly chrono: ChronoBar;
  /** Feedback and info, at the right-hand end of the toolbar. */
  private readonly help: HelpMenu;
  private readonly terrainPanel: TerrainPanel | null;
  private readonly history: HistoryPanel;
  private readonly dock: HTMLElement;
  private readonly statusBar: StatusBar;
  private readonly toolCursor: ToolCursor | null;
  private readonly toolHint = new ToolHint();
  private readonly center: HTMLElement;
  private readonly unsubscribe: (() => void)[] = [];

  private activeView: CameraViewName = 'perspective';
  /** Whether the shots browser has the frame, so its toolbar button can show itself pressed. */
  private shotsOpen = false;
  private frameHandle: number | null = null;
  private statusTimer: ReturnType<typeof setInterval> | null = null;
  /** Which tool the coachmark was last raised for, so picking one up can be told from holding it. */
  private hintedTool: ActiveToolName | null = null;
  /** The camera move now borrowing the pointer, or null while the tool has it. */
  private navCursor: CursorKind | null = null;
  /** The open document's name, kept because the info sheet and a report both want to name it. */
  private documentName = '';
  /**
   * Tools whose advanced commands the user has been seen using. From then on their card stays away
   * for the rest of the session: they have shown they know, and a hint that keeps coming back after
   * that is nagging rather than teaching.
   */
  private readonly hintsLearned = new Set<ActiveToolName>();
  /**
   * A reference-climate resolver for hosts that supply none — tests, and any headless HUD. Built
   * once and held, because a resolver's whole value is the cache behind it.
   */
  private ownResolver: FloraResolver | null = null;

  constructor(private readonly deps: HudDeps) {
    this.toolbar = new Toolbar((action) => this.handleAction(action));

    this.browser = new FloraBrowser({
      catalog: deps.editor.catalog,
      preferences: this.preferences,
      season: () => this.currentSeason(),
      dayOfYear: () => deps.environment.effectiveDayOfYear(),
      activeId: () => deps.editor.activeDefinitionId,
      onPick: (id) => this.pickPlant(id),
      // The record card parks against the viewport beside the palette, so it mounts in the HUD
      // root: the palette itself blurs its backdrop, which would pin a fixed child inside it.
      cardHost: () => this.root,
      // Resolved per call rather than captured: localizing the site swaps nothing here, and the
      // palette picks up the new profile on its next render.
      ...(deps.floraResolver
        ? { resolver: (): FloraResolver | null => (deps.localization?.localization() ? deps.floraResolver ?? null : null) }
        : {}),
      onOpenDatabase: () => this.database.toggle(),
    });

    // Built only when the application wired somewhere to place them. A palette that lists twenty
    // creatures and does nothing when one is clicked is worse than no palette.
    this.faunaBrowser = deps.fauna
      ? new FaunaBrowser({
          catalog: deps.fauna.catalog,
          preferences: this.preferences,
          armed: () => deps.fauna?.armed() ?? null,
          onPick: (definitionId, poseId) => {
            deps.fauna?.onPick(definitionId, poseId);
            this.requestRefresh();
          },
        })
      : null;

    this.objectBrowser = deps.objects
      ? new ObjectBrowser({
          catalog: deps.objects.catalog,
          preferences: this.preferences,
          armed: () => deps.objects?.armed() ?? null,
          onPick: (propId) => {
            deps.objects?.onPick(propId);
            this.requestRefresh();
          },
        })
      : null;

    // The database reads the resolver unconditionally, unlike the palette: an unlocalized site still
    // has a catalog worth charting, and the resolver answers against the reference climate when
    // there is no profile. What changes without a site is what the sheet *claims*, not whether it
    // draws — see `FloraDatabasePanel.siteLens`.
    this.database = new FloraDatabasePanel({
      catalog: deps.editor.catalog,
      preferences: this.preferences,
      season: () => this.currentSeason(),
      dayOfYear: () => deps.environment.effectiveDayOfYear(),
      // The sheet gets a handle on the calendar, not a copy of it: its scrubber writes the same
      // date the floating bar writes, so moving the year from the database moves the scene and the
      // bar, and moving it from the bar moves the database's cards. See `DateScrubber.ts`.
      environment: deps.environment,
      onDateChange: () => this.requestRefresh(),
      resolver: () => deps.floraResolver ?? this.fallbackResolver(),
      localization: () => deps.localization?.localization() ?? null,
      // Picked here, armed there, and shown selected in both: the database and the palette read the
      // same active plant off the editor rather than each remembering its own.
      activeId: () => deps.editor.activeDefinitionId,
      onPick: (id) => this.pickPlant(id),
      ...(deps.appVersion ? { appVersion: deps.appVersion } : {}),
      ...(deps.feedback ? { feedback: deps.feedback } : {}),
      onOpenChange: () => this.requestRefresh(),
    });

    // Above the palette rather than in the environment tray: localizing changes what every tile
    // below it says, so the control and its effect belong one glance apart. See `SitePanel.ts`.
    this.sitePanel = deps.localization
      ? new SitePanel({
          ...deps.localization,
          // `options` forwarded, not dropped: it carries the `auto` flag that tells the application
          // a profile was detected at startup rather than asked for, and losing it here means merely
          // opening the app marks the document dirty and mints a saved "Untitled site" from a
          // session in which the user did nothing.
          onLocalize: (localization, options) => {
            deps.localization?.onLocalize(localization, options);
            this.requestRefresh();
          },
          onConditions: (conditions) => {
            deps.localization?.onConditions(conditions);
            this.requestRefresh();
          },
        })
      : null;

    // Under the site bar, sharing its column and its collapse: both answer a question asked once at
    // the start of a session and never thought about again. See `LicensePanel.ts`.
    this.licensePanel = deps.license
      ? new LicensePanel({ ...deps.license })
      : null;

    this.inspector = new InspectorPanel({
      editor: deps.editor,
      catalog: deps.editor.catalog,
      preferences: this.preferences,
      season: () => this.currentSeason(),
      dayOfYear: () => deps.environment.effectiveDayOfYear(),
      site: () => deps.site?.() ?? null,
      ...(deps.semanticsFor ? { semanticsFor: deps.semanticsFor } : {}),
      ...(deps.siteSemantics ? { siteSemantics: deps.siteSemantics } : {}),
      ...(deps.onTagTool ? { onTagTool: (): void => deps.onTagTool?.(true) } : {}),
    });

    this.environmentPanel = new EnvironmentPanel({
      environment: deps.environment,
      onChange: () => this.requestRefresh(),
    });

    // Time and date are not tray settings — they are the light you are designing in, so they float
    // over the scene at the top centre instead of being docked with the rest of the environment.
    this.chrono = new ChronoBar({
      environment: deps.environment,
      onChange: () => this.requestRefresh(),
    });

    // Tool options mount at the top of this dock while a ground tool is live: it is the panel being
    // worked in, and the inspector has nothing to say about a brush.
    this.dock = el(
      'div',
      { class: 'lg-hud__dock' },
      this.inspector.root,
      this.environmentPanel.root,
    );

    this.terrainPanel = deps.terrainEditor
      ? new TerrainPanel({
          editor: deps.terrainEditor,
          host: () => this.dock,
          coverage: () => deps.groundCoverage?.() ?? null,
          onChange: () => this.requestRefresh(),
          ...(deps.onNewMaterial ? { onNewMaterial: deps.onNewMaterial } : {}),
          ...(deps.onRetireMaterial ? { onRetireMaterial: deps.onRetireMaterial } : {}),
          // The contour map's own controls live in the sculpt panel rather than in a tray of their
          // own: it is the tool that raises the map, and the settings for a map you are reading
          // belong beside the tool you are reading it for.
          displayUnit: () => (this.preferences.unit === 'feet' ? 'imperial' : 'metric'),
          ...(deps.topoVisible ? { topoVisible: deps.topoVisible } : {}),
          ...(deps.onToggleTopo ? { onToggleTopo: deps.onToggleTopo } : {}),
          ...(deps.topoLabelMode ? { topoLabelMode: deps.topoLabelMode } : {}),
          ...(deps.onCycleTopoLabels ? { onCycleTopoLabels: deps.onCycleTopoLabels } : {}),
          ...(deps.topoIntervalMeters ? { topoIntervalMeters: deps.topoIntervalMeters } : {}),
          ...(deps.topoIndexIntervalMeters
            ? { topoIndexIntervalMeters: deps.topoIndexIntervalMeters }
            : {}),
        })
      : null;

    this.statusBar = new StatusBar(this.preferences, () => this.requestRefresh(), deps.readout);

    // The centre column is the canvas showing through. The coachmark sits at the bottom of it,
    // where it is in the eyeline without covering the ground; anything mounted through
    // `mountViewportControl` positions itself against this column's corners, so it stays over the
    // view rather than over a panel however wide the trays are.
    this.center = el('div', { class: 'lg-hud__center' }, this.toolHint.root);

    this.root = el(
      'div',
      { class: 'lg-hud' },
      this.toolbar.root,
      el(
        'div',
        { class: 'lg-hud__body' },
        el(
          'div',
          { class: 'lg-hud__leftdock' },
          this.sitePanel?.root ?? null,
          this.licensePanel?.root ?? null,
          // Flora, then fauna, then objects: the order they are reached for, and the order the three
          // classes are described in throughout the docs.
          this.browser.root,
          this.faunaBrowser?.root ?? null,
          this.objectBrowser?.root ?? null,
        ),
        this.center,
        this.dock,
      ),
      this.statusBar.root,
      // Last child, and absolutely positioned: it floats over the docks rather than pushing them.
      this.chrono.root,
      // The database covers the docks rather than sitting between them, so it mounts at the root
      // and is hidden until asked for.
      this.database.root,
    );
    deps.container.appendChild(this.root);

    // Mounted in the HUD root because that is the one element already stretched over the canvas and
    // transparent to the pointer — and because the HUD is the only thing that knows, on every
    // refresh, which tool is actually in hand.
    this.toolCursor = deps.canvas
      ? new ToolCursor({ host: this.root, canvas: deps.canvas })
      : null;

    // Mounted in the HUD root rather than in a dock: it is a utility window that is shut most of
    // the time, and giving it a permanent column would cost the palette a third of its width. It
    // hangs under the toolbar's History group, directly below the button that opens it.
    this.history = new HistoryPanel({
      history: deps.editor.history,
      host: this.root,
      // Through the editor, not straight to `history.goto` — travelling can delete plants the
      // selection still names, and only the editor can put that right.
      goto: (index) => deps.editor.gotoHistory(index),
      onVisibilityChange: () => this.requestRefresh(),
    });

    // Built after the root because its dialogs mount into it, and mounted into the toolbar's own
    // utility slot so that signing in or out — which rebuilds the account control beside it — cannot
    // take it away with them.
    this.help = new HelpMenu({
      host: this.root,
      facts: () => this.aboutFacts(),
      context: () => this.reportContext(),
      ...(deps.feedback ? { channel: deps.feedback } : {}),
      ...(deps.onTour ? { onTour: deps.onTour } : {}),
    });
    this.toolbar.mountUtility(this.help.root);

    this.unsubscribe.push(deps.editor.onChange(() => this.requestRefresh()));
    this.unsubscribe.push(this.preferences.onChange(() => this.requestRefresh()));
    // The status bar's counters and frame rate change without any editor event.
    this.statusTimer = setInterval(() => this.updateStatus(), 500);

    this.refresh();
  }

  private fallbackResolver(): FloraResolver {
    this.ownResolver ??= unlocalizedResolver();
    return this.ownResolver;
  }

  /** Open the flora database, optionally on a particular lens. Also reached from the palette head. */
  openDatabase(): void {
    this.database.open();
    this.requestRefresh();
  }

  /** Whether the flora database is on screen. */
  get databaseOpen(): boolean {
    return this.database.isOpen;
  }

  /** The element the editor writes its placement/selection readout into. */
  get readoutElement(): HTMLElement {
    return this.statusBar.readout;
  }

  /**
   * Hide or show the whole HUD. Photo mode uses this to clear the frame — `hidden` rather than a
   * CSS class so the panels also leave the accessibility tree while the viewfinder is up.
   */
  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    // A hidden HUD hides its cursor glyph with it, so the canvas has to have its own pointer back —
    // otherwise photo mode would leave you with no cursor at all over the viewfinder.
    this.toolCursor?.setEnabled(visible);
  }

  /** Report the autosave state in the status bar. */
  setSaveStatus(status: AutosaveStatus): void {
    this.statusBar.setSaveIndicator(describeAutosave(status));
  }

  /** Show which document is being edited, under the product name in the toolbar. */
  setDocumentName(name: string): void {
    this.documentName = name;
    this.toolbar.setDocumentName(name);
  }

  /** Open the feedback form. Also reached from the toolbar and from the info sheet. */
  openFeedback(): void {
    this.help.openFeedback();
  }

  /** Open the info sheet — build, catalog, storage, and the keyboard. */
  openAbout(): void {
    this.help.openAbout();
  }

  /**
   * What the app can say about itself right now.
   *
   * Read on open rather than held: every line of it — the site, its plants, where it has been
   * localized to — changes while the dialog is shut, and a stale version number is the one thing an
   * info sheet must never show.
   */
  private aboutFacts(): AboutFacts {
    const { editor } = this.deps;
    const instances = editor.world.all();
    const place = this.placeLabel();
    return {
      appVersion: this.deps.appVersion ?? 'dev',
      catalogVersion: editor.catalog.version,
      catalogPlants: editor.catalog.size,
      storage: this.deps.storageLabel?.() ?? 'this browser',
      sitePlants: instances.length,
      siteSpecies: new Set(instances.map((instance) => instance.floraDefinitionId)).size,
      ...(this.documentName ? { siteName: this.documentName } : {}),
      ...(place ? { place } : {}),
    };
  }

  /**
   * The session, as lines attached to a report.
   *
   * Drawn from the same readings the status bar and the info sheet show, so a report describes the
   * app the sender was looking at rather than a second account of it. The tool in hand is here for
   * the same reason a screenshot usually is: half of "it did the wrong thing" is which thing was
   * being held at the time.
   */
  private reportContext(): string[] {
    const facts = this.aboutFacts();
    const site = this.deps.site?.() ?? null;
    const lines = [
      `Build: ${facts.appVersion}`,
      `Catalog: ${facts.catalogVersion} (${String(facts.catalogPlants)} plants)`,
      `Site: ${facts.siteName ?? 'unnamed'}${
        site ? ` — ${site.widthMeters.toFixed(0)} × ${site.lengthMeters.toFixed(0)} m, ${site.templateName}` : ''
      }`,
      `Planted: ${String(facts.sitePlants ?? 0)} plants in ${String(facts.siteSpecies ?? 0)} species`,
      `Localized: ${facts.place ?? 'not localized'}`,
      `Tool in hand: ${TOOL_LABELS[this.activeTool()]}`,
      `Kept in: ${facts.storage}`,
    ];
    return lines;
  }

  /** Where the site has been told it stands, in one line. Null while it is unlocalized. */
  private placeLabel(): string | null {
    const localization = this.deps.localization?.localization();
    if (!localization) return null;
    return `${localization.regionLabel} · zone ${zoneLabel(localization.hardinessZone)}`;
  }

  /** Tell the HUD which camera view is live, so the toolbar can show it pressed. */
  setActiveView(view: CameraViewName): void {
    this.activeView = view;
    this.requestRefresh();
  }

  /** Coalesce refreshes into the next animation frame. */
  requestRefresh(): void {
    if (this.frameHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (callback: FrameRequestCallback): number => setTimeout(() => callback(0), 16) as unknown as number;
    this.frameHandle = schedule(() => {
      this.frameHandle = null;
      this.refresh();
    });
  }

  refresh(): void {
    const { editor } = this.deps;
    // Both palettes draw the armed item as selected, and neither is told when the tool is put down
    // by something else — Escape, another tool taking the canvas — so they are re-read here.
    this.faunaBrowser?.refresh();
    this.objectBrowser?.refresh();
    const history = editor.history.state();
    const selectionCount = editor.selection.count;
    const tool = this.activeTool();

    // The toolbar shows the tool at arm's length; the cursor shows it under the hand.
    this.toolCursor?.setTool(this.cursorKind(tool));
    this.syncToolHint(tool);

    this.toolbar.update({
      activeTool: tool,
      activeView: this.activeView,
      canUndo: history.canUndo,
      canRedo: history.canRedo,
      undoLabel: history.undoLabel,
      redoLabel: history.redoLabel,
      hasSelection: selectionCount > 0,
      multiSelection: selectionCount > 1,
      gridVisible: this.deps.gridVisible?.() === true,
      topoVisible: this.deps.topoVisible?.() === true,
      historyOpen: this.history.isOpen,
      historyDepth: editor.history.depth,
      shotsOpen: this.shotsOpen,
    });

    this.browser.refresh();
    // No-ops while the sheet is shut, so this costs nothing in the ordinary case.
    this.database.refresh();
    this.inspector.refresh();
    this.terrainPanel?.refresh();
    this.sitePanel?.refresh();
    this.licensePanel?.refresh();
    // Only the winter section, not the whole tray: the environment panel rebuilds its DOM on
    // refresh and would drop a slider being dragged, but the date moves from the chrono bar and the
    // snow controls have to appear and disappear with it.
    this.environmentPanel.syncSeason();
    // Localizing the site writes latitude, and the tray is where that number is shown.
    this.environmentPanel.syncSite();
    // Cheap and DOM-stable, so it can follow every refresh — latitude edits in the tray move the
    // sunrise/sunset marks on its sky ribbon.
    this.chrono.refresh();
    this.updateStatus();
  }

  private updateStatus(): void {
    const { editor } = this.deps;
    const instances = editor.world.all();
    const tool = this.activeTool();
    this.statusBar.update({
      tool: tool === 'plant' ? `Plant · ${plantModeLabel(editor.plantMode)}` : TOOL_LABELS[tool],
      hint: tool === 'plant' ? this.plantHint() : TOOL_HINTS[tool],
      selectionCount: editor.selection.count,
      plantCount: instances.length,
      speciesCount: new Set(instances.map((i) => i.floraDefinitionId)).size,
      fps: this.deps.fps?.() ?? 0,
    });
  }

  /**
   * The plant tool's status line, which depends on the mode it is armed in.
   *
   * This is the standing answer to "which planting tool do I have"; the pointer glyph and the note
   * beside it are the glanceable and the momentary versions of the same fact. Three places rather
   * than one because they are read at three different distances, and the mode is decided *for* the
   * user by the species — which is exactly the kind of thing that needs saying more than once.
   */
  private plantHint(): string {
    const { editor } = this.deps;
    if (editor.effectivePlantMode !== 'paint') {
      return 'Click the ground to place · Ctrl for the brush · Esc to finish';
    }
    const brush = editor.plantBrush;
    return (
      `Drag to scatter · brush ${brush.radiusMeters.toFixed(1)} m, ` +
      `${brush.spacingMeters.toFixed(2)} m apart · Ctrl for one at a time`
    );
  }

  /**
   * Hand the pointer to a camera move for as long as it lasts, or hand it back when it ends.
   *
   * The HUD rather than the camera controller, because the pointer has exactly one owner and the
   * HUD is the thing that knows what else wants it. Cheap to call from a pointer move: it drops out
   * on a repeat, and a real change is one `setTool`, which is itself a no-op if the drawing is
   * already right.
   */
  setNavigating(action: NavAction | null): void {
    const kind = action === null ? null : navCursorKind(action);
    if (kind === this.navCursor) return;
    this.navCursor = kind;
    this.toolCursor?.setTool(this.cursorKind(this.activeTool()));
  }

  /**
   * What the pointer should be drawing.
   *
   * A camera move outranks the tool: while the view is moving, what the *next click* would do is a
   * question nobody is asking, and which of orbit, pan and zoom is under way is a question the
   * screen answers nowhere else (see `ToolCursor`).
   *
   * Sculpt and plant are the two tools whose pointer is finer-grained than their toolbar button:
   * both follow the mode *including* any held modifier, so pressing Alt turns the raise pointer into
   * the lower one you are about to get, and holding Ctrl turns the drift brush back into a single
   * sprout — rather than leaving the drawing arguing with the ring on the ground beside it.
   */
  private cursorKind(tool: ActiveToolName): CursorKind {
    if (this.navCursor !== null) return this.navCursor;
    if (tool === 'plant') return plantCursorKind(this.deps.editor.effectivePlantMode);
    if (tool !== 'sculpt') return tool;
    const mode = this.deps.terrainEditor?.effectiveSculptMode;
    return mode ? sculptCursorKind(mode) : tool;
  }

  /**
   * Raise the coachmark on the way into a tool that has one, and take it down on the way out.
   *
   * Driven off the edge rather than the state, so it appears when a tool is *picked up* and not on
   * every refresh while it is held — and so leaving and coming back offers it again, which is
   * exactly when someone who ignored it the first time might want it.
   */
  private syncToolHint(tool: ActiveToolName): void {
    const card = TOOL_COACHMARKS[tool];
    const target = card ? tool : null;
    if (target === this.hintedTool) return;
    this.hintedTool = target;
    if (!target || !card) {
      this.toolHint.dismiss();
      return;
    }
    if (this.hintsLearned.has(target)) return;
    this.toolHint.show(card.title, card.hints);
  }

  /**
   * Report that a ground-tool power command was used. Takes the coachmark down at once and keeps it
   * down: it exists to introduce these commands, and it has just been introduced to.
   */
  noteGroundShortcut(): void {
    this.noteToolShortcut('sculpt');
  }

  /** The same, for any tool that teaches shortcuts. */
  noteToolShortcut(tool: ActiveToolName): void {
    this.hintsLearned.add(tool);
    if (this.hintedTool === tool) this.toolHint.dismiss();
  }

  /**
   * Say one short thing beside the pointer, then take it back after a few seconds.
   *
   * Routed through the HUD rather than let each tool reach for the cursor, because the HUD is what
   * knows whether there is a pointer glyph to hang it off at all — a headless build and photo mode
   * both have none, and a tool that talked to the cursor directly would have to know that.
   */
  noteAtCursor(text: string): void {
    this.toolCursor?.note(text);
  }

  private activeTool(): ActiveToolName {
    const { editor } = this.deps;
    if (this.deps.wandActive?.() === true) return 'wand';
    if (this.deps.tagActive?.() === true) return 'tag';
    const ground = this.deps.terrainEditor?.activeTool;
    if (ground) return ground;
    if (editor.isPlacing) return 'plant';
    const mode = editor.activeTransformMode;
    if (mode === 'move' || mode === 'rotate' || mode === 'maturity') return mode;
    return 'select';
  }

  /** Season currently driving appearance — the blend's dominant anchor in calendar mode. */
  private currentSeason(): SimpleSeason {
    const state = this.deps.environment.state;
    if (state.date.mode === 'simple') return state.date.season;
    const blend = seasonBlend(this.deps.environment.effectiveDayOfYear());
    return blend.t < 0.5 ? blend.from : blend.to;
  }

  private pickPlant(definitionId: string): void {
    this.preferences.noteUsed(definitionId);
    // Picking from the palette arms placement directly — the Sims-style "click it, then click the
    // ground" flow — rather than merely highlighting a catalog row.
    this.deps.editor.startPlacement(definitionId);
    this.requestRefresh();
  }

  private handleAction(action: ToolbarAction): void {
    const { editor } = this.deps;
    // Toggling a view aid is not picking up a tool, so it must not take the canvas from the tool in
    // hand — switching the grid on to read a coordinate while the wand is armed is the whole point
    // of it. Everything else follows the rule below.
    const takesCanvas = !VIEW_AID_ACTIONS.includes(action);
    // Any action that is not a ground tool puts the canvas back under the flora editor first, so
    // the toolbar can never end up showing two tools pressed at once.
    if (takesCanvas && action !== 'paint' && action !== 'sculpt' && this.deps.terrainEditor?.isActive) {
      this.deps.onGroundTool?.(null);
    }
    // Same rule for the wand: any other tool takes the canvas back from it.
    if (takesCanvas && action !== 'wand' && this.deps.wandActive?.() === true) {
      this.deps.onWandTool?.(false);
    }
    // And for the tag tool, which holds the canvas the same way.
    if (takesCanvas && action !== 'tag' && this.deps.tagActive?.() === true) {
      this.deps.onTagTool?.(false);
    }
    switch (action) {
      case 'new':
        this.deps.onNewSite?.();
        break;
      case 'open':
        this.deps.onOpen?.();
        break;
      case 'save':
        this.deps.onSave?.();
        break;
      case 'save-as':
        this.deps.onSaveAs?.();
        break;
      case 'rename':
        this.deps.onRename?.();
        break;
      case 'undo':
        editor.undo();
        break;
      case 'redo':
        editor.redo();
        break;
      case 'history':
        this.toggleHistory();
        break;
      case 'select':
        editor.cancelPlacement();
        break;
      case 'plant':
        editor.togglePlacement();
        break;
      case 'move':
      case 'rotate':
      case 'maturity':
        editor.beginTransform(action);
        break;
      case 'paint':
      case 'sculpt':
        this.deps.onGroundTool?.(
          this.deps.terrainEditor?.activeTool === action ? null : action,
        );
        break;
      case 'wand':
        this.deps.onWandTool?.(this.deps.wandActive?.() !== true);
        break;
      case 'tag':
        this.deps.onTagTool?.(this.deps.tagActive?.() !== true);
        break;
      case 'duplicate':
        editor.duplicateSelected();
        break;
      case 'link':
        editor.linkSelected();
        break;
      case 'group':
        editor.groupSelected();
        break;
      case 'delete':
        editor.deleteSelected();
        break;
      case 'view-perspective':
        this.deps.onSetView('perspective');
        this.setActiveView('perspective');
        break;
      case 'view-top':
        this.deps.onSetView('top');
        this.setActiveView('top');
        break;
      case 'view-presentation':
        this.deps.onSetView('presentation');
        this.setActiveView('presentation');
        break;
      case 'focus':
        this.deps.onFocusSelection();
        break;
      case 'grid':
        this.deps.onToggleGrid?.();
        break;
      case 'topo':
        this.deps.onToggleTopo?.();
        break;
      case 'photo':
        this.deps.onTogglePhotoMode?.();
        break;
      case 'shots':
        this.deps.onOpenShots?.();
        break;
    }
    this.requestRefresh();
  }

  /** Open or close the operations history. Also reached from Ctrl+Shift+H and the toolbar. */
  toggleHistory(): void {
    this.history.toggle();
    this.requestRefresh();
  }

  /** Whether the operations history is on screen. */
  get historyOpen(): boolean {
    return this.history.isOpen;
  }

  /** Place the account control at the right-hand end of the toolbar. Null clears it. */
  mountAccountControl(element: HTMLElement | null): void {
    this.toolbar.mountTrailing(element);
  }

  /**
   * Mount a full-frame overlay in the HUD root — the shots browser is the one that uses it.
   *
   * The root rather than a dock because such a sheet covers the trays instead of sitting between
   * them, and because it must go away with the rest of the HUD when photo mode clears the frame: a
   * browser for shots has nothing to say while you are standing in the viewfinder that takes them.
   * The HUD's theme variables are declared on this element, so the overlay inherits them.
   */
  mountOverlay(element: HTMLElement | null): void {
    if (element === null) return;
    this.root.appendChild(element);
  }

  /** Whether an overlay has claimed the frame, so the toolbar can show its button pressed. */
  setShotsOpen(open: boolean): void {
    this.shotsOpen = open;
    this.requestRefresh();
  }

  /**
   * Mount a control over the canvas column — between the palette and the tray, above the coachmark.
   *
   * The column is the only element in the HUD whose box is the view itself, so a control that acts
   * on "what you are looking at" belongs in it rather than in the toolbar: it stays clear of both
   * trays at every width, and it goes away with the rest of the HUD when photo mode clears the
   * frame, without either side having to know about the other.
   */
  mountViewportControl(element: HTMLElement | null): void {
    if (element === null) return;
    this.center.appendChild(element);
  }

  dispose(): void {
    if (this.frameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = null;
    if (this.statusTimer !== null) clearInterval(this.statusTimer);
    this.statusTimer = null;
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.toolbar.dispose();
    this.browser.dispose();
    this.database.dispose();
    // Both hold something that outlives the DOM — the site panel a settle timer, the licence panel a
    // subscription to the key store — so neither can be left to the root's removal.
    this.sitePanel?.dispose();
    this.licensePanel?.dispose();
    this.inspector.dispose();
    this.terrainPanel?.dispose();
    this.history.dispose();
    this.environmentPanel.dispose();
    this.chrono.dispose();
    this.help.dispose();
    this.statusBar.dispose();
    this.toolCursor?.dispose();
    this.toolHint.dispose();
    this.root.remove();
  }
}

/**
 * Wording for the autosave indicator.
 *
 * "Saving…" is shown rather than swallowed: a save that is slow enough to see is worth seeing, and a
 * failed one has to say so, because that is the state where closing the tab loses work.
 */
function describeAutosave(status: AutosaveStatus): SaveIndicator {
  switch (status.phase) {
    case 'clean':
      return { label: '', title: '', tone: 'quiet' };
    case 'pending':
      return {
        label: 'Unsaved changes',
        title: 'Edits are waiting to be saved automatically.',
        tone: 'quiet',
      };
    case 'saving':
      return { label: 'Saving…', title: 'Writing the site to this browser.', tone: 'busy' };
    case 'saved':
      return {
        label: status.savedAt === null ? 'Saved' : `Saved ${clockTime(status.savedAt)}`,
        title: 'Every change is saved in this browser.',
        tone: 'quiet',
      };
    case 'error':
      return {
        label: 'Not saved',
        title: `Autosave failed: ${status.error ?? 'unknown error'}. Export a .landscape.json file to keep this site.`,
        tone: 'error',
      };
  }
}

function clockTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const TOOL_LABELS: Record<ActiveToolName, string> = {
  select: 'Select',
  plant: 'Plant',
  move: 'Move',
  rotate: 'Rotate',
  maturity: 'Maturity',
  paint: 'Paint ground',
  sculpt: 'Sculpt',
  wand: 'Wand',
  tag: 'Tag',
};

/**
 * The tools that put a card of shortcuts up when they are picked up.
 *
 * Only two, and both for the same reason: they are the tools whose most useful commands are held
 * modifiers and modified wheels, which is to say the commands no toolbar or panel can show you.
 * Everything else in the app is discoverable by looking at it.
 */
const TOOL_COACHMARKS: Partial<
  Record<ActiveToolName, { title: string; hints: readonly ShortcutHint[] }>
> = {
  sculpt: { title: 'Sculpt shortcuts', hints: SCULPT_SHORTCUTS },
  plant: { title: 'Planting shortcuts', hints: PLANT_SHORTCUTS },
};

const TOOL_HINTS: Record<ActiveToolName, string> = {
  // "Anything", not "a plant": the same click now takes creatures and objects too, and a hint that
  // named only plants would read as a limit rather than as an example.
  select: 'Click anything · drag to box-select · double-click for all of a kind · G move · R turn',
  // Replaced at runtime by `Hud.plantHint`, which knows which mode is armed. Kept here so the table
  // stays total over the tool names and nothing has to reason about a missing entry.
  plant: 'Click the ground to place · Esc to finish',
  move: 'Drag to reposition · Esc to cancel',
  rotate: 'Drag to spin · Esc to cancel',
  maturity: 'Drag to age the plant · Esc to cancel',
  paint: 'Drag to paint · Alt erases · Shift blends · Ctrl+wheel or [ ] sizes the brush',
  sculpt: 'Drag to shape · Alt inverts · Shift smooths · Ctrl+wheel or [ ] sizes the brush',
  wand: 'Click a plant, a building or the ground · drag to draw an area — then say what you want',
  tag: 'Click anything to say what it is — tags are sent with every AI render',
};
